<script setup>
import { ref, computed } from 'vue'
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover'
import { Input } from '@/components/ui/input'
import emojiGroups from 'unicode-emoji-json/data-by-group.json'
import emojiKeywords from 'emojilib'

const props = defineProps({
  modelValue: { type: String, default: null }
})

const emit = defineEmits(['update:modelValue'])

const open = ref(false)
const search = ref('')

// Slack-style quick picks pinned above the categories.
const QUICK = ['👍', '❤️', '😂', '🎉', '😮', '😢', '✅', '👀']

// data-by-group.json is already ordered Smileys & Emotion → … → Flags.
// Search text (name + emojilib keywords) is precomputed once at module level.
const categories = emojiGroups.map((group) => ({
  name: group.name,
  emojis: group.emojis.map((e) => ({
    char: e.emoji,
    search: `${e.name} ${(emojiKeywords[e.emoji] || []).join(' ')}`.toLowerCase(),
  })),
}))

const filteredCategories = computed(() => {
  const q = search.value.toLowerCase().trim()
  if (!q) return categories

  return categories
    .map(cat => ({
      name: cat.name,
      emojis: cat.emojis.filter(e => e.search.includes(q)),
    }))
    .filter(cat => cat.emojis.length > 0)
})

const selectEmoji = (emoji) => {
  emit('update:modelValue', emoji)
  open.value = false
  search.value = ''
}

const clearEmoji = () => {
  emit('update:modelValue', null)
  open.value = false
  search.value = ''
}
</script>

<template>
  <Popover v-model:open="open">
    <PopoverTrigger asChild>
      <slot>
        <button
          type="button"
          class="w-10 h-10 rounded-md border border-input bg-background flex items-center justify-center hover:bg-accent transition-colors cursor-pointer"
          :title="modelValue ? 'Change emoji' : 'Add emoji'"
        >
          <span v-if="modelValue" class="text-xl leading-none">{{ modelValue }}</span>
          <span v-else class="text-gray-400 text-lg leading-none">+</span>
        </button>
      </slot>
    </PopoverTrigger>
    <PopoverContent class="w-80 p-0" align="start" :sideOffset="8">
      <div class="p-2 border-b">
        <Input
          v-model="search"
          placeholder="Search all emoji"
          class="h-8 text-sm"
          @keydown.stop
        />
      </div>
      <div class="max-h-64 overflow-y-auto p-2">
        <div v-if="!search.trim()" class="grid grid-cols-7 gap-0.5 mb-1">
          <button
            v-for="emoji in QUICK"
            :key="emoji"
            type="button"
            class="w-9 h-9 flex items-center justify-center rounded hover:bg-accent cursor-pointer text-2xl"
            @click="selectEmoji(emoji)"
          >
            {{ emoji }}
          </button>
        </div>
        <template v-for="cat in filteredCategories" :key="cat.name">
          <div class="text-xs text-muted-foreground font-medium mb-1 mt-2 first:mt-0">{{ cat.name }}</div>
          <div class="grid grid-cols-7 gap-0.5">
            <button
              v-for="emoji in cat.emojis"
              :key="emoji.char"
              type="button"
              class="w-9 h-9 flex items-center justify-center rounded hover:bg-accent cursor-pointer text-2xl"
              @click="selectEmoji(emoji.char)"
            >
              {{ emoji.char }}
            </button>
          </div>
        </template>
        <div v-if="filteredCategories.length === 0" class="text-sm text-muted-foreground text-center py-4">
          No emojis found
        </div>
      </div>
      <div v-if="modelValue" class="border-t p-2">
        <button
          type="button"
          class="text-xs text-muted-foreground hover:text-foreground cursor-pointer"
          @click="clearEmoji"
        >
          Remove emoji
        </button>
      </div>
    </PopoverContent>
  </Popover>
</template>
